import React from 'react';
import { HeaderSchema } from '../../types/types';
import StatusTag from '../StatusTag';
import { IconClose, IconRegenerate } from '../../../../../../components/Icons';
import './Header.scss';
import { Whisper, Tooltip } from 'rsuite';

interface HeaderProps {
  data: HeaderSchema;
  beta?: boolean;
  className?: string;
  onRefresh?: () => void;
  onClose?: () => void; // Add onClose prop
  dateRange?: string | null;
}

/**
 * Header component for AI Analysis module
 *
 * Displays category, title, status tag, and date range
 */
export const Header: React.FC<HeaderProps> = ({ data, beta = true, className = '', onRefresh, onClose, dateRange }) => {
  // Handle case when data is undefined
  if (!data) {
    return (
      <div className="ai-analysis-header">
        <div className={`ai-analysis-header-container ${className}`}>
          <div className="ai-analysis-header-container__title">Loading analysis...</div>
          {onRefresh && (
            <Whisper
              trigger="hover"
              delay={500}
              delayClose={100}
              placement="top"
              speaker={<Tooltip>Refresh analysis</Tooltip>}
            >
              <button
                className="ai-analysis-header-container__refresh-button"
                onClick={onRefresh}
                aria-label="Refresh analysis"
              >
                <IconRegenerate />
              </button>
            </Whisper>
          )}
        </div>
      </div>
    );
  }

  const { category, title, status, dateRange: _dateRange } = data;

  return (
    <div className="ai-analysis-header">
      <div className={`ai-analysis-header-container ${className}`}>
        {category && <div className="ai-analysis-header-container__category">{category}</div>}

        <div className="ai-analysis-header-container__title-row">
          <Whisper
            trigger="hover"
            delay={800}
            delayClose={100}
            placement="top"
            speaker={<Tooltip>AI report summary is in beta, please note that it may not be accurate</Tooltip>}
          >
            {beta && <div className="ai-analysis-header-container__beta">beta</div>}
          </Whisper>
          <div className="ai-analysis-header-container__title">{title}</div>
          {status && <StatusTag label={status.label} severity={status.severity} />}
          {onRefresh && (
            <Whisper
              trigger="hover"
              delay={800}
              delayClose={100}
              placement="top"
              speaker={<Tooltip>Refresh analysis</Tooltip>}
            >
              <button
                className="ai-analysis-header-container__refresh-button"
                onClick={onRefresh}
                aria-label="Refresh analysis"
              >
                <IconRegenerate />
              </button>
            </Whisper>
          )}
        </div>

        {(dateRange || _dateRange) && (
          <div className="ai-analysis-header-container__date-range">{dateRange || _dateRange}</div>
        )}
      </div>
      {onClose && (
        <Whisper
          trigger="hover"
          delay={800}
          delayClose={100}
          placement="left"
          speaker={<Tooltip>Close analysis</Tooltip>}
        >
          <div className="ai-analysis-header-close" onClick={onClose}>
            <IconClose />
          </div>
        </Whisper>
      )}
    </div>
  );
};

export default Header;
